import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Address from "_shared/addresses/address";
import AddressSchema from "_shared/addresses/address-schema";
import Guid from "_shared/guids/guid";
import { z } from "zod";

export type CompanyProfileJSON = z.infer<typeof CompanyProfileSchema.CompanyProfileJSONZ>;
export type CompanyProfileParams = z.infer<typeof CompanyProfileSchema.CompanyProfileZ>;

export default class CompanyProfileSchema {
  public static readonly CompanyProfileJSONZ = z.object({
    name: z.string().nullish(),
    description: z.string().nullish(),
    location: AddressSchema.AddressJSONZ.nullish(),
    yearEstablished: z.number().nullish(),
    website: z.url().nullish(),
    numberOfEmployees: z.number().nullish(),
    categories: z.array(z.string()).nullish(),
    avatarId: z.uuid().nullish(),
    contactUserId: z.uuid().nullish(),
  });

  public static readonly CompanyProfileZ = z.object({
    name: z.string().optional(),
    description: z.string().optional(),
    location: z.instanceof(Address).optional(),
    yearEstablished: z.number().optional(),
    website: z.url().optional(),
    numberOfEmployees: z.number().optional(),
    categories: z.array(z.string()).optional(),
    avatarId: z.instanceof(Guid).optional(),
    contactUserId: z.instanceof(Guid).optional(),
  });

  public static validate(values: unknown): CompanyProfileParams {
    return parseOrThrow(CompanyProfileSchema.CompanyProfileZ, values, {
      logError: true,
      schemaName: "CompanyProfileSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): CompanyProfileJSON {
    return parseOrThrow(CompanyProfileSchema.CompanyProfileJSONZ, json, {
      logError: true,
      schemaName: "CompanyProfileSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is CompanyProfileParams {
    try {
      CompanyProfileSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is CompanyProfileJSON {
    try {
      CompanyProfileSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: CompanyProfileJSON): CompanyProfileParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(CompanyProfileSchema.CompanyProfileJSONZ, json, {
      logError: true,
      schemaName: "CompanyProfileSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return CompanyProfileSchema.codec.decode(parsed);
  }

  public static serialize(values: CompanyProfileParams): CompanyProfileJSON {
    if (isNullOrUndefined(values)) return values;
    const parsed = CompanyProfileSchema.validate(values);
    if (isNullOrUndefined(parsed)) return parsed;
    return CompanyProfileSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    CompanyProfileSchema.CompanyProfileJSONZ,
    CompanyProfileSchema.CompanyProfileZ,
    {
      decode: (data) => ({
        name: data.name ?? undefined,
        description: data.description ?? undefined,
        location: Address.fromJSON(data.location) ?? undefined,
        yearEstablished: data.yearEstablished ?? undefined,
        website: data.website ?? undefined,
        numberOfEmployees: data.numberOfEmployees ?? undefined,
        categories: data.categories ?? [],
        avatarId: Guid.fromJSON(data.avatarId) ?? undefined,
        contactUserId: Guid.fromJSON(data.contactUserId) ?? undefined,
      }),
      encode: (profile) => ({
        name: profile.name,
        description: profile.description,
        location: profile.location ? AddressSchema.serialize(profile.location) : undefined,
        yearEstablished: profile.yearEstablished,
        website: profile.website,
        numberOfEmployees: profile.numberOfEmployees,
        categories: profile.categories ?? [],
        avatarId: profile.avatarId?.toJSON(),
        contactUserId: profile.contactUserId?.toJSON(),
      }),
    }
  );
}
